import { useState, useRef, useEffect } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { Icon } from '../common'
import { useAuthStore } from '../../stores/auth'
import { api } from '../../services/api'
import { navItems, canUseNavItem } from '../../utils/navigation'

const ROLE_LABELS: Record<string, string> = {
  owner: 'Tenant Admin',
  tenant_admin: 'Tenant Admin',
  cloud_admin: 'Cloud Admin',
  manager: 'Manager',
  operator: 'Operator',
  viewer: 'Viewer',
}

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isPending, setIsPending] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setIsPending(true)
    try {
      await api.put('/auth/password', {
        current_password: currentPassword,
        new_password: newPassword,
      })
      setSuccess(true)
      setTimeout(onClose, 1500)
    } catch (err) {
      if (err && typeof err === 'object' && 'status' in err) {
        const apiErr = err as { status: number; message: string }
        if (apiErr.status === 403) {
          setError('Current password is incorrect')
        } else if (apiErr.status === 429) {
          setError('Too many attempts. Please wait and try again.')
        } else {
          setError(apiErr.message || 'Failed to change password')
        }
      } else {
        setError('Failed to change password')
      }
    } finally {
      setIsPending(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
        <div className="modal-header">
          <h3 className="modal-title">Change Password</h3>
          <button className="modal-close" onClick={onClose}>
            <Icon name="close" size={20} />
          </button>
        </div>

        {success ? (
          <div className="modal-body">
            <div className="login-info show" role="status" style={{ margin: '1rem 0' }}>
              Password changed successfully.
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && (
                <div className="login-error show" role="alert" style={{ marginBottom: '1rem' }}>
                  {error}
                </div>
              )}

              <div className="form-group">
                <label className="form-label" htmlFor="current-password">
                  Current Password
                </label>
                <input
                  type="password"
                  id="current-password"
                  className="form-input"
                  required
                  autoFocus
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="new-password">
                  New Password
                </label>
                <input
                  type="password"
                  id="new-password"
                  className="form-input"
                  required
                  minLength={8}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="confirm-new-password">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  id="confirm-new-password"
                  className="form-input"
                  required
                  minLength={8}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={isPending}>
                {isPending ? <span className="loading-spinner sm" /> : 'Change Password'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const refreshToken = useAuthStore((s) => s.refreshToken)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const role = user?.role ?? 'viewer'
  const email = user?.email ?? ''
  const initials = email ? email.slice(0, 2).toUpperCase() : 'US'

  // Close user menu on outside click
  useEffect(() => {
    if (!showUserMenu) return
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showUserMenu])

  const visibleItems = navItems.filter((item) => canUseNavItem(item, user?.role))

  const handleLogout = async () => {
    try {
      // Ends this session only; Settings offers signing out everywhere.
      await api.post('/auth/logout', { refresh_token: refreshToken })
    } catch {
      // Proceed with logout even if server call fails
    }
    logout()
    navigate('/login')
  }

  return (
    <>
      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <div className="sidebar-logo-icon">
              <Icon name="cloud" size={18} />
            </div>
            <span className="sidebar-logo-text">Sentyr Cloud</span>
          </div>
          {/* Mobile close button */}
          <button
            className="sidebar-close"
            onClick={onClose}
            aria-label="Close navigation"
          >
            <Icon name="close" size={20} />
          </button>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">
            {visibleItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `nav-item ${isActive ? 'active' : ''}`
                }
              >
                <Icon name={item.icon} size={20} className="nav-icon" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>

        <div className="sidebar-footer" ref={menuRef}>
          {showUserMenu && (
            <div className="user-dropdown">
              <button
                className="user-dropdown-item"
                onClick={() => {
                  setShowUserMenu(false)
                  setShowPasswordModal(true)
                }}
              >
                <Icon name="lock" size={18} />
                <span>Change Password</span>
              </button>
              <button className="user-dropdown-item danger" onClick={handleLogout}>
                <Icon name="logout" size={18} />
                <span>Sign Out</span>
              </button>
            </div>
          )}
          <button
            className="user-menu"
            onClick={() => setShowUserMenu(!showUserMenu)}
            aria-expanded={showUserMenu}
          >
            <div className="user-avatar">{initials}</div>
            <div className="user-info">
              <div className="user-name">{email || 'User'}</div>
              <div className="user-role">{ROLE_LABELS[role] || role}</div>
            </div>
            <Icon
              name={showUserMenu ? 'expand_more' : 'expand_less'}
              size={18}
              className="user-menu-arrow"
            />
          </button>
        </div>
      </aside>

      {showPasswordModal && (
        <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />
      )}
    </>
  )
}
